import React, { useMemo } from 'react';
import ResponsiveDrawer from '../Drawer/drawer';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Typography from '@mui/material/Typography';
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Paper from "@mui/material/Paper";
import { useNavigate } from 'react-router-dom';
import { useOrderDraft } from './OrderDraftContext';
import CartCard from '../../ReusableComponents/CartCard';

const Cart = () => {
    const drawerWidth = 220;
    const navigate = useNavigate();
    const { draft, clearCartForProduct } = useOrderDraft();
    const { cartItems, selectedCustomer } = draft;

    // Group items by SKU to render one card per product
    const groupedCart = useMemo(() => {
        const groups = {};
        cartItems.forEach(item => {
            if (!groups[item.sku]) {
                groups[item.sku] = {
                    sku: item.sku,
                    styleFactory: item.styleFactory,
                    color: item.color_on_box || item.Material1,
                    thumbnail: item.product_img,
                    Price1: item.Price1,
                    Price2: item.Price2,
                    Price3: item.Price3,
                    Size1: item.Size1,
                    Size2: item.Size2,
                    Size3: item.Size3,
                    items: []
                };
            }
            groups[item.sku].items.push(item);
        });
        return Object.values(groups);
    }, [cartItems]);

    // Calculate totals
    const grandTotals = useMemo(() => {
        let totalPairs = 0;
        let totalPrice = 0;
        cartItems.forEach(item => {
            totalPairs += item.unitQuantity;
            totalPrice += (item.unitQuantity * item.unitFinalPrice);
        });
        return { totalPairs, totalPrice };
    }, [cartItems]);

    const handleBack = () => {
        navigate('/SelectVariation');
    };

    const handleNext = () => {
        navigate('/ShippingDetails');
    };

    return (
        <div className="mx-">
            <ResponsiveDrawer heading="Selected Items - Create Order" />
            <Box
                sx={{
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    ml: { sm: `${drawerWidth - 25}px` },
                }}
                component="main"
            >
                <Toolbar />

                {/* Header bar */}
                <Box sx={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', mx: 5, mt: 1, mb: 3 }}>
                    <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={handleBack}>
                        Back
                    </Button>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: 'text.primary' }}>
                        Selected Items ({groupedCart.length})
                    </Typography>
                    <Box sx={{ width: 90 }} /> {/* balance layout */}
                </Box>

                <Box sx={{ px: 5, pb: 4 }}>
                    {groupedCart.length === 0 ? (
                        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" color="text.secondary">
                                Your cart is empty.
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                sx={{ mt: 2 }}
                                onClick={() => navigate('/SelectCustomer')}
                            >
                                Start Order / Select Customer
                            </Button>
                        </Paper>
                    ) : (
                        <Box>
                            {groupedCart.map(group => (
                                <CartCard
                                    key={group.sku}
                                    group={group}
                                    onDelete={clearCartForProduct}
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Footer totals bar */}
                {groupedCart.length > 0 && (
                    <Paper
                        elevation={4}
                        sx={{
                            position: 'fixed',
                            bottom: 0,
                            right: 0,
                            width: { sm: `calc(100% - ${drawerWidth}px)` },
                            p: 1.6,
                            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                            zIndex: 10,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: '#fafafa'
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 5 }}>
                            <Typography variant="subtitle1" fontWeight="700" color="text.secondary">
                                Unit Total: <span style={{ color: '#1a1a1a', fontWeight: '800' }}>{grandTotals.totalPairs} pairs</span>
                            </Typography>
                            <Typography variant="subtitle1" fontWeight="700" color="text.secondary">
                                Grand Total: <span style={{ color: '#1976d2', fontWeight: '800' }}>${grandTotals.totalPrice.toFixed(2)}</span>
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            color="success"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            onClick={handleNext}
                            sx={{ fontWeight: 'bold', px: 4 }}
                        >
                            Next
                        </Button>
                    </Paper>
                )}
            </Box>
        </div>
    );
};

export default Cart;